export const formatDateLabel = (dateValue) => {
  if (!dateValue) return "Select Date";
  const date = new Date(dateValue);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTimeLabel = (date) =>
  new Date(date).toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

export const formatDateForApi = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getEditionApiValue = (edition) => {
  if (edition?.slug) return String(edition.slug).toLowerCase().trim();
  const fallback = edition?.apiEdition || edition?.name || "";
  return String(fallback).toLowerCase().trim().replace(/\s+/g, "-");
};

// PDF preview loads through an <iframe>, which cannot send custom headers, so the
// access key must be appended to the URL as a query param. Value comes from the
// build-time env var (change the param name here if the backend expects a
// different one, e.g. `key`/`token`).
const EPAPER_PDF_API_KEY = process.env.REACT_APP_EPAPER_PDF_API_KEY || "";
const EPAPER_PDF_API_KEY_PARAM = "apiKey";

export const withPdfApiKey = (url) => {
  if (!url || !EPAPER_PDF_API_KEY) return url || "";
  if (new RegExp(`[?&]${EPAPER_PDF_API_KEY_PARAM}=`).test(url)) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${EPAPER_PDF_API_KEY_PARAM}=${encodeURIComponent(EPAPER_PDF_API_KEY)}`;
};

export const buildPdfUrl = (edition) => withPdfApiKey(edition.pdfPath || "");

const isPlaceholderPreview = (url) => /noissu/i.test(url || "");

export const getEditionPreview = (edition) => {
  const pdfPath = withPdfApiKey(edition.pdfPath || "");
  const allPagesPdfPaths = Array.isArray(edition.allPagesPdfPaths)
    ? edition.allPagesPdfPaths.filter(Boolean)
    : [];
  const previewUrl = edition.previewUrl || "";
  const mapPreview =
    edition.mapPreview && !isPlaceholderPreview(edition.mapPreview) ? edition.mapPreview : "";
  return { pdfPath, allPagesPdfPaths, previewUrl, mapPreview };
};

export const getAllPagesPdfPaths = (item) =>
  (Array.isArray(item?.allPagesPdfPaths)
    ? item.allPagesPdfPaths.filter(Boolean)
    : Array.isArray(item?.all_pages_pdf_paths)
    ? item.all_pages_pdf_paths.filter(Boolean)
    : []
  ).map(withPdfApiKey);

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isEditionUpdateComplete = (row) => {
  if (!row) return false;
  if (row.isNotPublished) return false;
  if (row.status === "Pending") return false;
  return row.status === "Success" || row.status === "Failed";
};

export const normalizeStatus = (value) => {
  const raw = `${value || ""}`.toLowerCase();
  if (raw === "success" || raw === "updated" || raw === "completed") return "Success";
  if (raw === "failed" || raw === "error") return "Failed";
  return "Pending";
};

export const statusToApiValue = (statusLabel) => {
  if (statusLabel === "Success") return "completed";
  if (statusLabel === "Failed") return "failed";
  if (statusLabel === "Pending") return "pending";
  return "";
};

export const statusDisplayLabel = (statusLabel, isNotPublished = false) => {
  if (isNotPublished) return "Not Published";
  if (statusLabel === "Success") return "Successful";
  return statusLabel;
};

export const normalizeEditionRow = (item, index) => {
  const name = item.editionName || item.edition || item.name || item.title || `Edition ${index + 1}`;
  const status = normalizeStatus(item.status);
  const sourceComment = `${item.comments || item.comment || item.message || ""}`.toLowerCase();
  const isNotPublished =
    Boolean(item.isNotPublished) || (status === "Pending" && sourceComment.includes("not published"));
  return {
    id: item.eid || item.id || `${name}-${index}`,
    name,
    apiEdition: (item.slug || item.edition || item.editionCode || name)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-"),
    slug: item.slug || item.city_slug || item.citySlug || "",
    pdfPath: item.pdfPath || item.pdf_path || "",
    previewUrl: item.previewUrl || item.preview_url || "",
    mapPreview: item.mapPreview || item.map_preview || "",
    allPagesPdfPaths: getAllPagesPdfPaths(item),
    editionInsertTimeMs: item.editionInsertTimeMs ?? item.edition_insert_time_ms ?? item.durationMs ?? 0,
    editionInsertTimeFormatted: item.editionInsertTimeFormatted || item.edition_insert_time_formatted || "",
    isNotPublished,
    status,
    comments:
      item.comments ||
      item.comment ||
      (status === "Success" ? "Successfully updated" : status === "Failed" ? "Update failed" : "Pending"),
    updatedAt: new Date(item.updatedAt || item.lastUpdated || item.modifiedAt || Date.now()),
  };
};

export const parseDashboardPayload = (payload = {}) => {
  const dashboardData = payload?.data || payload;
  const summary = dashboardData?.summary || payload?.summary || {};
  const pagination = dashboardData?.pagination || payload?.pagination || {};
  const candidateRows = Array.isArray(dashboardData)
    ? dashboardData
    : dashboardData?.editions ||
      dashboardData?.data ||
      dashboardData?.rows ||
      dashboardData?.items ||
      dashboardData?.result ||
      [];
  const rows = Array.isArray(candidateRows)
    ? candidateRows
    : candidateRows && typeof candidateRows === "object"
    ? Object.values(candidateRows)
    : [];
  const normalized = rows.map((item, index) => normalizeEditionRow(item, index));
  const totalEditions =
    summary?.totalEditions ??
    pagination?.total ??
    dashboardData?.total ??
    dashboardData?.totalCount ??
    dashboardData?.count ??
    normalized.length;
  return {
    summary,
    pagination,
    normalized,
    totalEditions,
    successCount:
      summary?.successfullyUpdated ?? normalized.filter((item) => item.status === "Success").length,
    failedCount:
      summary?.failedUpdates ?? normalized.filter((item) => item.status === "Failed").length,
    pendingCount:
      summary?.pendingUpdates ??
      normalized.filter((item) => item.status === "Pending" && !item.isNotPublished).length,
    notPublishedCount:
      summary?.notPublishedUpdates ?? normalized.filter((item) => item.isNotPublished).length,
    lastSyncIso: summary?.lastSyncIso || summary?.lastSync || "",
    totalExecutionTime: summary?.totalExecutionTimeFormatted || summary?.avgUpdateTimeFormatted || "0s",
  };
};

export const extractUpdateMessage = (error) => {
  const payload = error?.response?.data || {};
  if (error?.response?.status === 409) {
    const runningJob = payload?.runningJob || {};
    const base = payload?.message || "Another Jagran update is already in progress.";
    const details = [
      runningJob?.pid ? `PID: ${runningJob.pid}` : "",
      runningJob?.date ? `Date: ${runningJob.date}` : "",
      runningJob?.slug ? `Edition: ${runningJob.slug}` : "",
      runningJob?.startedAt ? `Started: ${formatDateTimeLabel(runningJob.startedAt)}` : "",
    ]
      .filter(Boolean)
      .join(" | ");
    return details ? `${base} (${details})` : base;
  }
  return (
    payload?.message ||
    payload?.error ||
    error?.message ||
    "Edition update failed. Please retry."
  );
};

export const metricCards = (
  total,
  successCount,
  failedCount,
  totalExecutionTime,
  lastSyncAt,
  pendingCount = 0,
  notPublishedCount = 0
) => {
  const successRate = total > 0 ? Math.round((successCount / total) * 1000) / 10 : 0;
  const failedHint = `~ Failed: ${failedCount} | Pending: ${pendingCount} | Not Published: ${notPublishedCount}`;

  return [
    {
      label: "Total Editions",
      value: total,
      hint: `~ ${total} editions for selected date`,
      cardClass: "",
      valueClass: "text-gray-900",
    },
    {
      label: "Successfully Updated",
      value: successCount,
      hint: `~ ${successRate}% success rate`,
      cardClass: "bg-emerald-50 border-emerald-100",
      valueClass: "text-emerald-600",
    },
    {
      label: "Failed Updates",
      value: failedCount,
      hint: failedHint,
      cardClass: "bg-red-50 border-red-100",
      valueClass: "text-red-600",
    },
    {
      label: "Total Execution Time",
      value: totalExecutionTime || "0s",
      hint: `~ Last sync ${formatDateTimeLabel(lastSyncAt)}`,
      cardClass: "",
      valueClass: "text-gray-900",
    },
  ];
};
